import {
  cardanoAddressSchema,
  isOwnedCardanoPaymentAddress,
} from '@pkg/cardano'
import { and, eq, sql } from 'drizzle-orm'
import { z } from 'zod'
import { accountCardanoSchema } from '../querySchema'
import {
  accountTable,
  cardanoEventIndexTable,
  cardanoPaymentAddressTable,
  cardanoSyncCheckpointTable,
} from '../schema'
import type { DatabaseWithTransaction } from '../types'
import {
  type CardanoEvent,
  cardanoEventSchema,
  getCardanoEventSourceId,
} from './cardanoSchemas'

/**
 * Base/Reward は Stake 単位、Enterprise はアドレス単位で冪等登録する。
 * @param params DB と公開アドレス一覧
 * @returns 入力順に重複を除いた登録済み Account ID 一覧
 */
export const registerCardanoAccounts = async (params: {
  database: DatabaseWithTransaction
  addresses: readonly string[]
}): Promise<readonly number[]> => {
  const addresses = z.array(cardanoAddressSchema).parse(params.addresses)
  const ids = new Set<number>()
  await params.database.transaction([
    (tx) => [
      (async () => {
        for (const address of addresses) {
          await tx
            .insert(accountTable)
            .values({
              type: 'cardano',
              category: 'cardano',
              group: address.stakeAddress ?? address.address,
              name: address.address,
              cardanoAddress: address.address,
              cardanoStakeAddress: address.stakeAddress,
              syncing: true,
              cardanoIndexComplete: false,
            })
            .onConflictDoNothing()
          const [row] = await tx
            .select({ id: accountTable.id })
            .from(accountTable)
            .where(
              address.stakeAddress === null
                ? eq(accountTable.cardanoAddress, address.address)
                : eq(accountTable.cardanoStakeAddress, address.stakeAddress),
            )
          if (row === undefined)
            throw new Error('Cardano account registration failed')
          ids.add(row.id)
          // 登録元のPayment Addressは探索APIに依存せず保持する。
          if (address.type === 'base' || address.type === 'enterprise') {
            await tx
              .insert(cardanoPaymentAddressTable)
              .values({ accountId: row.id, address: address.address })
              .onConflictDoNothing()
          }
        }
      })(),
    ],
  ])
  return [...ids]
}

/**
 * Cardano の所有アドレス、探索位置、未解析索引を取得する。
 * @param params DB と Account ID
 * @returns 検証済み Account と探索・解析状態
 */
export const getCardanoSyncState = async (params: {
  database: DatabaseWithTransaction
  accountId: number
}) => {
  const db = params.database.database
  const [accountRow] = await db
    .select()
    .from(accountTable)
    .where(eq(accountTable.id, params.accountId))
  const account = accountCardanoSchema.parse(accountRow)
  const addresses = await db
    .select()
    .from(cardanoPaymentAddressTable)
    .where(eq(cardanoPaymentAddressTable.accountId, params.accountId))
  const checkpoints = await db
    .select()
    .from(cardanoSyncCheckpointTable)
    .where(eq(cardanoSyncCheckpointTable.accountId, params.accountId))
  const pending = await db
    .select()
    .from(cardanoEventIndexTable)
    .where(
      and(
        eq(cardanoEventIndexTable.accountId, params.accountId),
        eq(cardanoEventIndexTable.analyzed, false),
      ),
    )
    .orderBy(cardanoEventIndexTable.timestamp, cardanoEventIndexTable.sourceId)
  const rewards = await db
    .select({
      payoutEpoch: cardanoEventIndexTable.payoutEpoch,
      rewardAmount: cardanoEventIndexTable.rewardAmount,
    })
    .from(cardanoEventIndexTable)
    .where(
      and(
        eq(cardanoEventIndexTable.accountId, params.accountId),
        eq(cardanoEventIndexTable.kind, 'reward'),
      ),
    )
  return {
    account,
    addresses: addresses.map((row) => row.address),
    checkpoints,
    pending,
    rewards,
  }
}

/**
 * 完全に取得・検証できたページとその checkpoint を同じ transaction で保存する。
 * 既に解析済みの Tx は増分取得で未解析に戻さない。
 * @param params DB、Account、ストリーム、ページ、探索結果
 * @returns 保存が完了したときに解決する Promise
 */
export const saveCardanoSyncPage = async (params: {
  database: DatabaseWithTransaction
  accountId: number
  stream: string
  page: number
  complete: boolean
  events?: readonly CardanoEvent[]
  paymentAddresses?: readonly string[]
}): Promise<void> => {
  const page = z.number().int().positive().safe().parse(params.page)
  const stream = z.string().min(1).max(256).parse(params.stream)
  const events = z.array(cardanoEventSchema).parse(params.events ?? [])
  if (events.length > 100)
    throw new Error('Cardano event page exceeds 100 items')
  await params.database.transaction([
    (tx) => [
      (async () => {
        const [row] = await tx
          .select()
          .from(accountTable)
          .where(eq(accountTable.id, params.accountId))
        const account = accountCardanoSchema.parse(row)
        for (const address of params.paymentAddresses ?? []) {
          if (
            !(account.cardanoStakeAddress === null
              ? address === account.cardanoAddress
              : isOwnedCardanoPaymentAddress({
                  address,
                  stakeAddress: account.cardanoStakeAddress,
                }))
          ) {
            throw new Error('Not an owned Cardano key payment address')
          }
          await tx
            .insert(cardanoPaymentAddressTable)
            .values({ accountId: account.id, address })
            .onConflictDoNothing()
        }
        for (const event of events) {
          const sourceId = getCardanoEventSourceId({
            event,
            stakeAddress: account.cardanoStakeAddress,
          })
          await tx
            .insert(cardanoEventIndexTable)
            .values({
              accountId: account.id,
              sourceId,
              kind: event.kind,
              txHash: event.kind === 'tx' ? event.txHash : undefined,
              payoutEpoch:
                event.kind === 'reward' ? event.payoutEpoch : undefined,
              rewardAmount:
                event.kind === 'reward' ? event.rewardAmount : undefined,
              timestamp: new Date(event.timestamp),
              analyzed: false,
            })
            .onConflictDoNothing()
        }
        // 短い末尾ページは新規レコード追加時に再取得する。
        await tx
          .insert(cardanoSyncCheckpointTable)
          .values({
            accountId: account.id,
            stream,
            page: params.complete ? page : page + 1,
            complete: params.complete,
          })
          .onConflictDoUpdate({
            target: [
              cardanoSyncCheckpointTable.accountId,
              cardanoSyncCheckpointTable.stream,
            ],
            set: {
              page: params.complete ? page : page + 1,
              complete: params.complete,
            },
          })
        await tx
          .update(accountTable)
          .set({ syncing: true, cardanoIndexComplete: false })
          .where(eq(accountTable.id, account.id))
      })(),
    ],
  ])
}

/**
 * 探索の開始・成功を保存し、未解析索引とは独立して同期状態を更新する。
 * @param params DB、Account ID、必要な全探索が完了したか
 * @returns 同期状態の更新が完了した Promise
 */
export const setCardanoIndexComplete = async (params: {
  database: DatabaseWithTransaction
  accountId: number
  complete: boolean
}): Promise<void> => {
  await params.database.transaction([
    (tx) => [
      tx
        .update(accountTable)
        .set({
          cardanoIndexComplete: params.complete,
          ...(params.complete ? { lastSyncedAt: new Date() } : {}),
          syncing: params.complete
            ? sql`EXISTS (SELECT 1 FROM ${cardanoEventIndexTable} WHERE ${cardanoEventIndexTable.accountId} = ${params.accountId} AND ${cardanoEventIndexTable.analyzed} = 0)`
            : true,
        })
        .where(
          and(
            eq(accountTable.id, params.accountId),
            eq(accountTable.type, 'cardano'),
          ),
        ),
    ],
  ])
}

/**
 * Cardano の探索位置と解析索引を削除して完全再同期を要求する。
 * Action 本体は成功した再解析時に置換するため、失敗時も既存履歴が残る。
 * @param params DB と Account ID
 * @returns 再同期状態の保存完了を表す Promise
 */
export const resetCardanoAccountSync = async (params: {
  database: DatabaseWithTransaction
  accountId: number
}): Promise<void> => {
  await params.database.transaction([
    (tx) => [
      (async () => {
        const [row] = await tx
          .select()
          .from(accountTable)
          .where(eq(accountTable.id, params.accountId))
        accountCardanoSchema.parse(row)
        await tx
          .delete(cardanoEventIndexTable)
          .where(eq(cardanoEventIndexTable.accountId, params.accountId))
        await tx
          .delete(cardanoSyncCheckpointTable)
          .where(eq(cardanoSyncCheckpointTable.accountId, params.accountId))
        await tx
          .delete(cardanoPaymentAddressTable)
          .where(eq(cardanoPaymentAddressTable.accountId, params.accountId))
        await tx
          .update(accountTable)
          .set({
            lastSyncedAt: null,
            syncing: true,
            cardanoIndexComplete: false,
          })
          .where(eq(accountTable.id, params.accountId))
      })(),
    ],
  ])
}
